import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {normalizeLoxoPodUrl} from './podUrl';
import * as Roaming from './roamingSettings';
import {subscribeToRoamingSettingsChanges} from './roamingSettingsSync';

export type RoamingSettingsState = {
  emailSyncOnSend: boolean;
  podUrl: string | undefined;
  refreshToken: string | undefined;
  jwt: string | undefined;
};

type AuthMemoryState = {
  jwt: string | undefined;
};

const readStateFromOffice = (): RoamingSettingsState => {
  const rawSync = Roaming.getEmailSyncOnSend();
  return {
    emailSyncOnSend: rawSync ?? true,
    podUrl: Roaming.getPodUrl(),
    refreshToken: Roaming.getRefreshToken(),
    jwt: Roaming.getJwt(),
  };
};

export type RoamingSettingsContextValue = RoamingSettingsState & {
  jwt: string | undefined;
  setEmailSyncOnSend: (value: boolean) => Promise<void>;
  setPodUrl: (value: string) => Promise<void>;
  setRefreshToken: (value: string) => Promise<void>;
  setJwt: (value: string) => Promise<void>;
  saveAuth: (params: {
    podUrl: string;
    jwt: string;
    refreshToken: string;
  }) => Promise<void>;
  clearJwtAndRefreshTokens: () => Promise<void>;
  refreshFromParent?: () => Promise<RoamingSettingsState>;
};

export const RoamingSettingsContext =
  createContext<RoamingSettingsContextValue | null>(null);

export const RoamingSettingsProvider = ({children}: {children: ReactNode}) => {
  const [state, setState] = useState<RoamingSettingsState>(readStateFromOffice);
  const [authMemory, setAuthMemory] = useState<AuthMemoryState>({
    jwt: undefined,
  });

  useEffect(() => {
    if (
      Roaming.getEmailSyncOnSend() === undefined &&
      Roaming.getRoamingSettings()
    ) {
      void (async () => {
        await Roaming.setEmailSyncOnSend(true);
      })();
    }
  }, []);

  useEffect(
    () =>
      subscribeToRoamingSettingsChanges(() => {
        setState(readStateFromOffice());
      }),
    []
  );

  const setEmailSyncOnSend = useCallback(async (value: boolean) => {
    setState((prev) => ({...prev, emailSyncOnSend: value}));
    try {
      await Roaming.setEmailSyncOnSend(value);
    } catch {
      const emailSyncOnSend = Roaming.getEmailSyncOnSend() ?? true;
      setState((prev) => ({...prev, emailSyncOnSend}));
    }
  }, []);

  const setPodUrl = useCallback(async (value: string) => {
    const normalizedValue = normalizeLoxoPodUrl(value);
    setState((prev) => ({...prev, podUrl: normalizedValue}));
    try {
      await Roaming.setPodUrl(normalizedValue);
    } catch {
      setState((prev) => ({...prev, podUrl: Roaming.getPodUrl()}));
    }
  }, []);

  const setRefreshToken = useCallback(async (value: string) => {
    setState((prev) => ({...prev, refreshToken: value}));
    try {
      await Roaming.setRefreshToken(value);
    } catch {
      setState((prev) => ({...prev, refreshToken: Roaming.getRefreshToken()}));
    }
  }, []);

  const setJwt = useCallback(async (value: string) => {
    setAuthMemory({jwt: value});
  }, []);

  const saveAuth = useCallback(
    async (params: {podUrl: string; jwt: string; refreshToken: string}) => {
      const normalizedPodUrl = normalizeLoxoPodUrl(params.podUrl);
      setAuthMemory({jwt: params.jwt});
      setState({
        ...readStateFromOffice(),
        podUrl: normalizedPodUrl,
        jwt: params.jwt,
        refreshToken: params.refreshToken,
      });
      try {
        await Roaming.saveAuthToRoaming({
          podUrl: normalizedPodUrl,
          jwt: params.jwt,
          refreshToken: params.refreshToken,
        });
        setState(readStateFromOffice());
      } catch (error) {
        setAuthMemory({jwt: undefined});
        setState(readStateFromOffice());
        throw error;
      }
    },
    []
  );

  const clearJwtAndRefreshTokens = useCallback(async () => {
    setAuthMemory({jwt: undefined});
    setState((prev) => ({...prev, jwt: undefined, refreshToken: undefined}));
    try {
      await Roaming.clearRefreshToken();
    } catch {
      setState(readStateFromOffice());
    }
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      jwt: authMemory.jwt,
      setEmailSyncOnSend,
      setPodUrl,
      setRefreshToken,
      setJwt,
      saveAuth,
      clearJwtAndRefreshTokens,
    }),
    [
      state,
      authMemory.jwt,
      setEmailSyncOnSend,
      setPodUrl,
      setRefreshToken,
      setJwt,
      saveAuth,
      clearJwtAndRefreshTokens,
    ]
  );

  return (
    <RoamingSettingsContext.Provider value={value}>
      {children}
    </RoamingSettingsContext.Provider>
  );
};

export const useRoamingSettings = (): RoamingSettingsContextValue => {
  const ctx = useContext(RoamingSettingsContext);
  if (ctx == null) {
    throw new Error(
      'useRoamingSettings must be used within RoamingSettingsProvider'
    );
  }
  return ctx;
};
